import { observer } from 'mobx-react';
import { FC, useEffect, useState, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { ControlledDater } from '@monorepo/controlled/src/components/controlled-dater/controlled-dater';
import { ControlledTable } from '@monorepo/controlled/src/components/table-wrapper/table-wrapper';
import { Page, Pagebar, PagebarTitle, PagebarActions } from '@monorepo/base/src/components/page/page';
import { useDealActions } from '../../../modules/hooks/actions/deals.actions';
import { SideEffectKeys } from '../../../modules/hooks/apis/use-performance';
import { getFilterObject, PerformancesColumnsSnakeCase } from '../../../modules/hooks/apis/use-reports-list';
import { useList } from '../../../modules/hooks/tools/use-list';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { useMergeWithPerformance } from '../../../modules/hooks/tools/use-merge-with-performance';
import { DealModel } from '../../../modules/models/deal.model';
import { usePerformanceEntity } from '../../../modules/hooks/apis/use-performance-entity';
import { useDealHeaders } from '../../../modules/hooks/theaders/deals.headers';
import { PerformanceModel } from '../../../modules/models/performance.model';
import { IAdminxStore } from '../../../modules/stores';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { useRoute } from '../../../modules/hooks/tools/use-route';
import { usePageSettings } from '@monorepo/tools/src/lib/hooks/tools/use-page-settings';

export const Deals: FC = observer(() => {
	const { dealStore } = useStores<IAdminxStore>();
	const { currentRouteWithoutSpecialChars } = useRoute();

	const performanceStore = dealStore.getPerformance();

	const currentFilters = usePageSettings()?.tableStore?.getFiltersStore(currentRouteWithoutSpecialChars)?.getCurrentFilters();
	const [filters, setFilters] = useState<any>(JSON.stringify(currentFilters));
	const [isLoading, setIsLoading] = useState<boolean>(false);
	useList<DealModel>(dealStore.getListStore());

	usePerformanceEntity(performanceStore, {
		reportName: 'adxDeals',
		filters: getFilterObject(currentFilters || new Map(), 'adxDeals'),
	});

	useEffect(() => {
		setIsLoading(true);
		const loadingInterval = setInterval(() => {
			if (!performanceStore.getIsLoading() && !dealStore.getListStore().getIsLoading()) {
				setIsLoading(false);
				setFilters(JSON.stringify(currentFilters));
				clearInterval(loadingInterval);
			}
		}, 100);

		return () => {
			clearInterval(loadingInterval);
		};
	}, [JSON.stringify(currentFilters), performanceStore, dealStore]);

	// Get all deals and filter to only include deals that have performance data
	const allDeals = dealStore.getListStore().getList();
	const performanceItems = performanceStore.getData()?.getData() || [];

	const filteredDeals = useMemo(() => {
		if (!performanceItems || performanceItems.length === 0) {
			return [];
		}

		const performanceDealIds = new Set(
			performanceItems
				.map(perf => {
					const dealId = (perf as any).dealId;
					return dealId?.toString();
				})
				.filter(Boolean)
		);

		return allDeals.filter(deal => {
			if (!deal.deal_id) {
				return false;
			}
			return performanceDealIds.has(deal.deal_id.toString());
		});
	}, [allDeals, performanceItems]);

	const { data, summary } = useMergeWithPerformance<DealModel & PerformanceModel>({
		performanceStore,
		data: filteredDeals,
		key: PerformancesColumnsSnakeCase.DealId,
		foreignKey: SideEffectKeys.DealId,
		attachExtraParams(row) {
			return {
				preferred_deal_id: row.deal_id,
				deal_name: row.deal_name,
				name: row.deal_name,
				serving_status: row.serving_status,
				deal_type: row.deal_type,
				formats: row.formats,
				proposal_id: row.proposal_id,
				proposal_name: row.proposal_name,
				publisher_profile: row.publisher_profile,
				cpm: row.cpm,
				deal_resource_type: row.deal_resource_type,
			};
		},
	});
	const { SelectedActions, SegmentActions, FiltersActions } = useDealActions();

	const { columns } = useDealHeaders({ summary });

	return (
		<Page>
			<Pagebar>
				<PagebarTitle>Deals</PagebarTitle>
				<PagebarActions>
					<ControlledDater />
				</PagebarActions>
			</Pagebar>
			<ControlledTable
				currentRoute={currentRouteWithoutSpecialChars}
				columns={columns}
				debugProps={{ dataAttrs: [new DataAttribute('id', 'deals_table')] }}
				data={data}
				isLoading={isLoading || JSON.stringify(currentFilters) !== filters}
				isSummary={true}>
				{() => {
					return {
						SelectedActions,
						SegmentActions,
						FiltersActions,
					};
				}}
			</ControlledTable>
			<Outlet />
		</Page>
	);
});
